import type { HeaderColor, HeaderProps } from '@presentation/components';
import { Logo } from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import storeFrontIcon from '@assets/storefront.svg';
import type { FC } from 'react';
import type { ButtonColor } from '../Button';
import { LanguageSelector } from './components/LanguageSelector';
import { MobileHeader } from './components/MobileHeader';
import * as Styled from './styled';

export * from './types';

const BUTTON_COLOR_PER_HEADER_COLOR: Record<HeaderColor, ButtonColor> = {
  blue: 'light',
  purple: 'light',
  light: 'blue',
};

export const Header: FC<HeaderProps> = (props) => {
  const {
    links = [],
    onHomeClick,
    buttons = [],
    languageLinks,
    canUseMobileNav = true,
    unfinishedApplicationComponent,
    languageSelectorButtons,
    logoType = 'tipaw',
    color = 'light',
    showStoreLink = true,
    showStickyBlueBanner = true,
    onStickyBlueBannerClick,
    onStoreNavbarClick,
    ...rest
  } = props;

  const { translate } = useTranslation();

  return (
    <>
      <Styled.Wrapper color={color} showStickyBlueBanner={showStickyBlueBanner} {...rest}>
        {showStickyBlueBanner ? (
          <Styled.BlueBanner onClick={onStickyBlueBannerClick}>
            <span>{translate('header.stickyBlueBanner.text')}</span>{' '}
            <Styled.BlueBannerCta>{translate('header.stickyBlueBanner.cta')}</Styled.BlueBannerCta>
          </Styled.BlueBanner>
        ) : null}
        <Styled.NavContainer showStickyBlueBanner={showStickyBlueBanner}>
          <Styled.LeftSideItems>
            <Logo
              css={Styled.logoStyle}
              color={color === 'light' ? 'dark' : 'light'}
              type={logoType}
              onClick={onHomeClick}
            />
            <Styled.MainContainer hideOnMobile={canUseMobileNav}>
              <div>
                {links.map((link, index) => (
                  <Styled.MainLink key={index} onClick={link.onClick} headercolor={color}>
                    {link.label}
                    {!!link.links && !!link.links.length && (
                      <Styled.LinkDropdownContent data-dropdown-content headercolor={color}>
                        {link.links.map((sublink, subindex) => (
                          <Styled.SubLink
                            headercolor={color}
                            to={sublink.link}
                            key={subindex}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              sublink.onClick?.();
                            }}
                          >
                            {sublink.label}
                          </Styled.SubLink>
                        ))}
                        {unfinishedApplicationComponent}
                      </Styled.LinkDropdownContent>
                    )}
                  </Styled.MainLink>
                ))}
                {showStoreLink ? (
                  <Styled.StoreLink onClick={onStoreNavbarClick}>
                    {translate('common.buttons.storeHeader')}
                    <Styled.StoreIcon src={storeFrontIcon} alt="store" />
                  </Styled.StoreLink>
                ) : null}
              </div>
            </Styled.MainContainer>
          </Styled.LeftSideItems>
          {canUseMobileNav && <MobileHeader {...props} />}

          <Styled.MainContainer hideOnMobile={canUseMobileNav}>
            <div>
              {buttons.map((button, index) => (
                <Styled.ButtonStyled
                  headercolor={color}
                  key={index}
                  link={button.link}
                  onClick={button.onClick}
                  color={BUTTON_COLOR_PER_HEADER_COLOR[color]}
                  outlined={button.style !== 'main'}
                  loading={button.loading ?? false}
                  loadingProps={{
                    color: button.style !== 'main' ? 'dark' : 'light',
                    type: 'spinning-circles',
                  }}
                >
                  {button.label}
                </Styled.ButtonStyled>
              ))}
            </div>
            <LanguageSelector
              css={Styled.languageSelectorStyle}
              color={color === 'light' ? 'dark' : 'light'}
              languageSelectorButtons={languageSelectorButtons}
            />
          </Styled.MainContainer>
        </Styled.NavContainer>
      </Styled.Wrapper>
    </>
  );
};
